import { z } from "zod";

export const memberRoleSchema = z.enum(["owner", "admin", "editor", "contributor", "reviewer"]);
export type MemberRole = z.infer<typeof memberRoleSchema>;

export const publicationTypeSchema = z.enum(["school_magazine", "yearbook", "graduation_book"]);
export type PublicationType = z.infer<typeof publicationTypeSchema>;

export const vibePresetSchema = z.enum(["modern", "fun", "premium", "colourful", "minimal", "academic", "islamic", "playful"]);
export type VibePreset = z.infer<typeof vibePresetSchema>;

export const publicationStatusSchema = z.enum(["draft", "review", "approved", "exported", "archived"]);
export type PublicationStatus = z.infer<typeof publicationStatusSchema>;

export const pageStatusSchema = z.enum(["planned", "needs_content", "draft", "review", "approved", "issue"]);
export type PageStatus = z.infer<typeof pageStatusSchema>;

export const pageTypeSchema = z.enum([
  "cover", "contents", "principal_message", "chairman_message", "about_school",
  "vision_mission", "teachers", "class_page", "student_achievement", "event",
  "sports", "gallery", "graduation", "sponsor", "acknowledgement",
  "generic_editorial", "back_cover"
]);
export type PageType = z.infer<typeof pageTypeSchema>;

export const schoolTypeSchema = z.enum([
  "preschool", "primary_school", "secondary_school", "international_school",
  "college", "university", "education_group"
]);

export const onboardingInputSchema = z.object({
  organizationName: z.string().trim().min(2).max(120),
  schoolName: z.string().trim().min(2).max(120),
  schoolType: schoolTypeSchema,
  country: z.string().trim().min(2).max(80).default("Malaysia"),
  academicSystem: z.string().trim().min(2).max(80),
  primaryLanguage: z.string().trim().min(2).max(40),
  secondaryLanguage: z.string().trim().max(40).nullable().default(null),
  primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  secondaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  website: z.union([z.url(), z.literal("")]).default(""),
  description: z.string().trim().max(1000).default(""),
  principalName: z.string().trim().max(120).default("")
});
export type OnboardingInput = z.infer<typeof onboardingInputSchema>;

export const sectionPlanSchema = z.object({
  key: z.string().trim().regex(/^[a-z0-9_]+$/),
  title: z.string().trim().min(1).max(100),
  pageType: pageTypeSchema,
  pageCount: z.number().int().min(1).max(100)
});
export type SectionPlan = z.infer<typeof sectionPlanSchema>;

export const createPublicationInputSchema = z.object({
  schoolId: z.uuid(),
  type: publicationTypeSchema,
  title: z.string().trim().min(2).max(160),
  subtitle: z.string().trim().max(200).default(""),
  academicYear: z.string().trim().min(4).max(20),
  targetPageCount: z.number().int().min(8).max(300),
  language: z.string().trim().min(2).max(40),
  vibePreset: vibePresetSchema,
  vibeDescription: z.string().trim().max(1000).default(""),
  creativePrompt: z.string().trim().max(4000).default(""),
  creativeBrief: z.string().trim().max(600).default(""),
  creativeAssumptions: z.array(z.string().trim().min(1).max(240)).max(8).default([]),
  sections: z.array(sectionPlanSchema).min(2).max(40)
}).superRefine((value, ctx) => {
  const planned = value.sections.reduce((sum, section) => sum + section.pageCount, 0);
  if (planned !== value.targetPageCount) {
    ctx.addIssue({ code: "custom", path: ["sections"], message: `Section pages total ${planned}; expected ${value.targetPageCount}.` });
  }
});
export type CreatePublicationInput = z.infer<typeof createPublicationInputSchema>;

export const publicationBriefRequestSchema = z.object({
  schoolId: z.uuid(),
  prompt: z.string().trim().min(10).max(4000)
}).strict();
export type PublicationBriefRequest = z.infer<typeof publicationBriefRequestSchema>;

export const publicationBriefSchema = z.object({
  summary: z.string().trim().min(1).max(600),
  assumptions: z.array(z.string().trim().min(1).max(240)).max(8),
  plan: createPublicationInputSchema
}).strict();
export type PublicationBrief = z.infer<typeof publicationBriefSchema>;

export const reorderPagesInputSchema = z.object({
  pageIds: z.array(z.uuid()).min(1).max(300),
  expectedVersion: z.number().int().nonnegative()
}).refine((value) => new Set(value.pageIds).size === value.pageIds.length, {
  message: "Page IDs must be unique.", path: ["pageIds"]
});
export type ReorderPagesInput = z.infer<typeof reorderPagesInputSchema>;

export const duplicatePageInputSchema = z.object({ expectedOrderVersion: z.number().int().nonnegative() }).strict();
export const deletePagesInputSchema = z.object({
  pageIds: z.array(z.uuid()).min(1).max(50),
  expectedOrderVersion: z.number().int().nonnegative()
}).strict().refine((value) => new Set(value.pageIds).size === value.pageIds.length, { message: "Choose each page once.", path: ["pageIds"] });
export interface DuplicatePageResult { pageId: string; pageNumber: number; orderVersion: number }
export interface DeletePagesResult { deletedCount: number; pageCount: number; orderVersion: number }

export interface PageThumbnailObject {
  type: "text" | "image" | "shape";
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  text: string | null;
}
export interface PublicationPageThumbnail { pageId: string; pageNumber: number; background: string; objects: PageThumbnailObject[] }

export const editablePageStatusSchema = z.enum(["needs_content", "draft", "review", "issue"]);

export interface PageFieldDefinition {
  key: string;
  label: string;
  kind: "short_text" | "long_text" | "string_list";
  placeholder: string;
  required?: boolean;
}

const editorialFields = [
  { key: "headline", label: "Headline", kind: "short_text", placeholder: "A year of growing together", required: true },
  { key: "subheadline", label: "Supporting line", kind: "short_text", placeholder: "Celebrating every learner" },
  { key: "description", label: "Story", kind: "long_text", placeholder: "Write the story for this page…" }
] as const satisfies ReadonlyArray<PageFieldDefinition>;

export const PAGE_FIELD_DEFINITIONS: Record<PageType, ReadonlyArray<PageFieldDefinition>> = {
  cover: [
    { key: "headline", label: "Magazine title", kind: "short_text", placeholder: "Our School Magazine", required: true },
    { key: "subtitle", label: "Theme or subtitle", kind: "short_text", placeholder: "Growing together" },
    { key: "academicYear", label: "Academic year", kind: "short_text", placeholder: "2026" }
  ],
  contents: [
    { key: "headline", label: "Heading", kind: "short_text", placeholder: "Inside this edition", required: true },
    { key: "description", label: "Introduction", kind: "long_text", placeholder: "A short welcome to this edition…" }
  ],
  principal_message: [
    { key: "headline", label: "Heading", kind: "short_text", placeholder: "A message from our principal", required: true },
    { key: "name", label: "Principal name", kind: "short_text", placeholder: "Dr. Sarah Ahmad" },
    { key: "position", label: "Position", kind: "short_text", placeholder: "Principal" },
    { key: "message", label: "Message", kind: "long_text", placeholder: "Write or paste the principal's message…", required: true }
  ],
  chairman_message: [
    { key: "headline", label: "Heading", kind: "short_text", placeholder: "From our chairman", required: true },
    { key: "name", label: "Chairman name", kind: "short_text", placeholder: "Dato' Ahmad" },
    { key: "position", label: "Position", kind: "short_text", placeholder: "Chairman" },
    { key: "message", label: "Message", kind: "long_text", placeholder: "Write or paste the chairman's message…", required: true }
  ],
  about_school: editorialFields,
  vision_mission: editorialFields,
  teachers: [
    { key: "headline", label: "Heading", kind: "short_text", placeholder: "The people who inspire us", required: true },
    { key: "description", label: "Introduction", kind: "long_text", placeholder: "Introduce your teaching team…" },
    { key: "names", label: "Teacher names", kind: "string_list", placeholder: "One name per line" }
  ],
  class_page: [
    { key: "className", label: "Class name", kind: "short_text", placeholder: "Year 4 Amanah", required: true },
    { key: "teacherName", label: "Teacher", kind: "short_text", placeholder: "Teacher Aisyah" },
    { key: "classQuote", label: "Class quote", kind: "short_text", placeholder: "Together we shine" },
    { key: "description", label: "Class story", kind: "long_text", placeholder: "Share this class's memorable year…" },
    { key: "studentNames", label: "Student names", kind: "string_list", placeholder: "One student per line" }
  ],
  student_achievement: [
    { key: "headline", label: "Achievement", kind: "short_text", placeholder: "Young Innovator Award", required: true },
    { key: "name", label: "Student name", kind: "short_text", placeholder: "Nur Aisyah" },
    { key: "description", label: "Story", kind: "long_text", placeholder: "Describe the achievement…" }
  ],
  event: editorialFields,
  sports: editorialFields,
  gallery: editorialFields,
  graduation: editorialFields,
  sponsor: editorialFields,
  acknowledgement: editorialFields,
  generic_editorial: editorialFields,
  back_cover: [
    { key: "headline", label: "Closing line", kind: "short_text", placeholder: "Until next year" },
    { key: "description", label: "Contact or note", kind: "long_text", placeholder: "School contact details or a closing message…" }
  ]
};

const pageContentValueSchema = z.union([
  z.string().max(12_000),
  z.array(z.string().trim().min(1).max(160)).max(150),
  z.null()
]);

export const pageContentSchema = z.record(z.string().min(1).max(60), pageContentValueSchema)
  .refine((value) => Object.keys(value).length <= 30, "Page content has too many fields.");

export function validatePageContent(pageType: PageType, value: unknown): ReturnType<typeof pageContentSchema.safeParse> {
  const allowed = new Set(["sectionTitle", ...PAGE_FIELD_DEFINITIONS[pageType].map((field) => field.key)]);
  return pageContentSchema.superRefine((content, ctx) => {
    for (const key of Object.keys(content)) {
      if (!allowed.has(key)) ctx.addIssue({ code: "custom", path: [key], message: `Field ${key} is not supported for ${pageType}.` });
    }
  }).safeParse(value);
}

export const updatePageInputSchema = z.object({
  pageType: pageTypeSchema,
  title: z.string().trim().min(1).max(160),
  status: editablePageStatusSchema,
  templateId: z.string().trim().regex(/^[a-z0-9_]+$/).max(80),
  content: pageContentSchema,
  designOverrides: z.object({
    backgroundStyle: z.enum(["clean", "tinted", "bold"]).optional(),
    photoStyle: z.enum(["editorial", "rounded", "full_bleed"]).optional(),
    density: z.enum(["spacious", "balanced", "compact"]).optional(),
    textAlign: z.enum(["left", "center"]).optional()
  }).strict(),
  expectedVersion: z.number().int().nonnegative()
});
export type UpdatePageInput = z.infer<typeof updatePageInputSchema>;

export const aiVibeCommandSchema = z.object({
  command: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().nonnegative()
}).strict();
export type AiVibeCommand = z.infer<typeof aiVibeCommandSchema>;

export const aiGeneratedPageEditSchema = z.object({
  summary: z.string().trim().min(1).max(500),
  title: z.string().trim().min(1).max(160).optional(),
  templateId: z.string().trim().regex(/^[a-z0-9_]+$/).max(80).optional(),
  content: pageContentSchema.optional(),
  designOverrides: z.object({
    backgroundStyle: z.enum(["clean", "tinted", "bold"]).optional(),
    photoStyle: z.enum(["editorial", "rounded", "full_bleed"]).optional(),
    density: z.enum(["spacious", "balanced", "compact"]).optional(),
    textAlign: z.enum(["left", "center"]).optional()
  }).strict().optional()
}).strict();
export type AiGeneratedPageEdit = z.infer<typeof aiGeneratedPageEditSchema>;

export interface AiVibeResult {
  pageId: string;
  rowVersion: number;
  summary: string;
  model: string;
  provider: string;
  costUsd: number;
}

export const aiPlanRequestSchema = z.object({
  command: z.string().trim().min(3).max(500)
}).strict();
export type AiPlanRequest = z.infer<typeof aiPlanRequestSchema>;

export const aiGeneratedPlanActionSchema = aiGeneratedPageEditSchema.omit({ summary: true }).extend({
  pageId: z.uuid(),
  rationale: z.string().trim().min(1).max(300)
}).strict();

export const aiGeneratedPublicationPlanSchema = z.object({
  summary: z.string().trim().min(1).max(500),
  actions: z.array(aiGeneratedPlanActionSchema).min(1).max(20)
}).strict().superRefine((plan, ctx) => {
  const ids = plan.actions.map((action) => action.pageId);
  if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", path: ["actions"], message: "A plan may suggest only one action per page." });
});
export type AiGeneratedPublicationPlan = z.infer<typeof aiGeneratedPublicationPlanSchema>;

export const aiPlanActionSchema = z.object({
  actionId: z.uuid(),
  pageId: z.uuid(),
  pageNumber: z.number().int().positive().max(300),
  pageTitle: z.string().trim().min(1).max(160),
  rationale: z.string().trim().min(1).max(300),
  expectedVersion: z.number().int().nonnegative(),
  patch: updatePageInputSchema
}).strict();
export type AiPlanAction = z.infer<typeof aiPlanActionSchema>;

export interface AiPlanProposal {
  id: string;
  publicationId: string;
  command: string;
  summary: string;
  status: "pending" | "applied" | "dismissed";
  actions: AiPlanAction[];
  selectedActionIds: string[];
  model: string;
  provider: string;
  costUsd: number;
  createdAt: string;
  appliedAt: string | null;
}

export const applyAiPlanSchema = z.object({
  selectedActionIds: z.array(z.uuid()).min(1).max(20)
}).strict().superRefine((value, ctx) => {
  if (new Set(value.selectedActionIds).size !== value.selectedActionIds.length) ctx.addIssue({ code: "custom", path: ["selectedActionIds"], message: "Select each action once." });
});
export type ApplyAiPlan = z.infer<typeof applyAiPlanSchema>;

export interface ApplyAiPlanResult {
  proposalId: string;
  updatedPages: Array<{ pageId: string; rowVersion: number }>;
}

export interface PageTemplate {
  id: string;
  pageType: PageType;
  name: string;
  description: string;
  layoutKey: string;
  defaultContent: Record<string, string | string[] | null>;
  regions: Record<string, unknown>;
  photoRegions: string[];
  designTokens: Record<string, unknown>;
}

export function photoSlotCount(layoutKey: string, regionKey: string): number {
  if (regionKey === "logo" || regionKey === "portrait" || regionKey === "hero" || regionKey === "photo" || regionKey === "classPhoto" || regionKey === "teamPhoto") return 1;
  return ({ cover_mosaic: 6, class_mosaic: 5, event_grid: 4, gallery_mosaic: 6, achievement_wall: 6, team_grid: 6, sponsor_grid: 12 } as Record<string, number>)[layoutKey] ?? (regionKey === "activities" ? 3 : 1);
}

export const placeMediaAssetSchema = z.object({
  pageId: z.uuid(),
  assetId: z.uuid(),
  regionKey: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/).max(60),
  position: z.number().int().min(0).max(11).default(0)
}).strict();

export interface AutoDesignPublicationResult {
  publicationId: string;
  pagesDesigned: number;
  placementsCreated: number;
  availablePhotos: number;
}

export const schoolDesignTemplateInputSchema = z.object({
  publicationId: z.uuid(),
  pageId: z.uuid(),
  name: z.string().trim().min(2).max(100),
  description: z.string().trim().max(500).default(""),
  tags: z.array(z.string().trim().min(1).max(30)).max(12).default([])
}).strict();
export type SchoolDesignTemplateInput = z.infer<typeof schoolDesignTemplateInputSchema>;

export const applyDesignTemplateInputSchema = z.object({
  publicationId: z.uuid(),
  pageId: z.uuid(),
  source: z.enum(["system", "school"]),
  templateId: z.string().trim().min(1).max(100),
  expectedVersion: z.number().int().nonnegative()
}).strict();
export type ApplyDesignTemplateInput = z.infer<typeof applyDesignTemplateInputSchema>;

export const publicationRemixStyleSchema = z.enum(["brand_clean", "editorial", "celebration", "premium"]);
export type PublicationRemixStyle = z.infer<typeof publicationRemixStyleSchema>;
export const publicationRemixInputSchema = z.object({ publicationId: z.uuid(), style: publicationRemixStyleSchema }).strict();

export interface DesignTemplateSummary {
  id: string;
  source: "system" | "school";
  pageType: PageType;
  name: string;
  description: string;
  layoutKey: string;
  tags: string[];
  preview: { background: string; primary: string; secondary: string; objectCount: number; imageCount: number };
  createdAt: string | null;
}

export interface DesignQualityIssue {
  code: string;
  severity: "improvement" | "warning";
  category: "hierarchy" | "imagery" | "spacing" | "readability" | "consistency";
  message: string;
  pageId: string;
  pageNumber: number;
}

export interface PublicationDesignQuality {
  publicationId: string;
  score: number;
  level: "needs_work" | "good" | "excellent";
  pagesChecked: number;
  issues: DesignQualityIssue[];
}

export interface PublicationRemixResult { publicationId: string; style: PublicationRemixStyle; pagesUpdated: number }

export const publicationMasterStyleInputSchema = z.object({
  publicationId: z.uuid(),
  headerText: z.string().trim().max(100).default(""),
  footerText: z.string().trim().max(160).default(""),
  showPageNumbers: z.boolean().default(true),
  startPageNumber: z.number().int().min(1).max(300).default(2),
  pageNumberPosition: z.enum(["left", "center", "right"]).default("right")
}).strict();
export type PublicationMasterStyleInput = z.infer<typeof publicationMasterStyleInputSchema>;
export interface PublicationMasterStyle extends PublicationMasterStyleInput { updatedAt: string | null }

export const mediaCategorySchema = z.enum(["classes", "teachers", "events", "sports", "graduation", "achievements", "general"]);
export type MediaCategory = z.infer<typeof mediaCategorySchema>;

export const mediaUploadQuerySchema = z.object({
  schoolId: z.uuid(),
  publicationId: z.uuid().optional(),
  category: mediaCategorySchema.default("general"),
  sourceAssetId: z.uuid().optional()
});

export const imageAdjustmentsSchema = z.object({
  brightness: z.number().int().min(-100).max(100).default(0),
  contrast: z.number().int().min(-100).max(100).default(0),
  saturation: z.number().int().min(-100).max(100).default(0),
  warmth: z.number().int().min(-100).max(100).default(0),
  grayscale: z.number().int().min(0).max(100).default(0),
  blur: z.number().min(0).max(20).default(0)
}).strict();
export type ImageAdjustments = z.infer<typeof imageAdjustmentsSchema>;

export interface MediaAsset {
  id: string;
  schoolId: string;
  publicationId: string | null;
  filename: string;
  mimeType: "image/jpeg" | "image/png" | "image/webp";
  fileSize: number;
  width: number | null;
  height: number | null;
  category: MediaCategory;
  caption: string;
  status: "uploading" | "ready" | "failed";
  usageCount: number;
  sourceAssetId: string | null;
  transformSpec: ImageAdjustments | null;
  createdAt: string;
  contentUrl: string;
}

export interface PageAssetPlacement {
  id: string;
  pageId: string;
  assetId: string;
  regionKey: string;
  position: number;
  crop: { x: number; y: number; zoom: number };
  asset: MediaAsset;
}

export const canvasObjectSchema = z.object({
  type: z.enum(["Textbox", "IText", "Text", "Rect", "Circle", "Triangle", "Line", "Image"]),
  elementId: z.uuid(),
  groupId: z.uuid().optional(),
  name: z.string().trim().min(1).max(120),
  left: z.number().finite().min(-4000).max(8000),
  top: z.number().finite().min(-6000).max(12000),
  width: z.number().finite().positive().max(8000).optional(),
  height: z.number().finite().positive().max(12000).optional(),
  scaleX: z.number().finite().min(0.01).max(100).default(1),
  scaleY: z.number().finite().min(0.01).max(100).default(1),
  angle: z.number().finite().min(-3600).max(3600).default(0),
  opacity: z.number().finite().min(0).max(1).default(1),
  visible: z.boolean().default(true),
  selectable: z.boolean().default(true),
  fill: z.string().max(120).optional(),
  stroke: z.string().max(120).optional(),
  strokeWidth: z.number().finite().min(0).max(100).optional(),
  rx: z.number().finite().min(0).max(1000).optional(),
  ry: z.number().finite().min(0).max(1000).optional(),
  text: z.string().max(30_000).optional(),
  fontFamily: z.string().trim().max(100).optional(),
  fontSize: z.number().finite().min(4).max(600).optional(),
  fontWeight: z.union([z.string().max(30), z.number().finite().min(100).max(900)]).optional(),
  fontStyle: z.enum(["normal", "italic", "oblique"]).optional(),
  textAlign: z.enum(["left", "center", "right", "justify"]).optional(),
  lineHeight: z.number().finite().min(0.5).max(5).optional(),
  charSpacing: z.number().finite().min(-1000).max(5000).optional(),
  assetId: z.uuid().optional(),
  imageFit: z.enum(["contain", "cover"]).optional(),
  focalX: z.number().finite().min(0).max(1).optional(),
  focalY: z.number().finite().min(0).max(1).optional(),
  cropX: z.number().finite().min(0).max(20_000).optional(),
  cropY: z.number().finite().min(0).max(20_000).optional(),
  sourceWidth: z.number().finite().positive().max(20_000).optional(),
  sourceHeight: z.number().finite().positive().max(20_000).optional(),
  src: z.string().max(500).optional()
}).strict().superRefine((value, ctx) => {
  if (value.type === "Image" && !value.assetId) ctx.addIssue({ code: "custom", path: ["assetId"], message: "Images require a media asset." });
  if ((value.type === "Textbox" || value.type === "IText" || value.type === "Text") && value.text === undefined) ctx.addIssue({ code: "custom", path: ["text"], message: "Text elements require text." });
});
export type CanvasObject = z.infer<typeof canvasObjectSchema>;

export const canvasDocumentSchema = z.object({
  schemaVersion: z.literal(1),
  width: z.number().int().min(200).max(4000),
  height: z.number().int().min(200).max(6000),
  background: z.string().max(120),
  objects: z.array(canvasObjectSchema).max(300)
}).strict();
export type CanvasDocument = z.infer<typeof canvasDocumentSchema>;

export const saveCanvasInputSchema = z.object({
  expectedVersion: z.number().int().nonnegative(),
  document: canvasDocumentSchema
}).strict();
export type SaveCanvasInput = z.infer<typeof saveCanvasInputSchema>;

export interface PageCanvasDocument {
  pageId: string;
  publicationId: string;
  rowVersion: number;
  document: CanvasDocument;
  updatedAt: string | null;
}

export interface RebuildPublicationCanvasesResult {
  publicationId: string;
  pagesUpdated: number;
}

const canvasHexSchema=z.string().regex(/^#[0-9A-Fa-f]{6}$/);
export const canvasAiGeneratedActionSchema=z.discriminatedUnion("type",[
  z.object({type:z.literal("apply_style"),style:z.enum(["brand_clean","editorial","celebration","midnight"]),rationale:z.string().trim().min(1).max(240)}).strict(),
  z.object({type:z.literal("set_page_background"),color:canvasHexSchema,rationale:z.string().trim().min(1).max(240)}).strict(),
  z.object({type:z.literal("align_element"),elementId:z.uuid(),alignment:z.enum(["left","center-x","right","top","center-y","bottom"]),rationale:z.string().trim().min(1).max(240)}).strict(),
  z.object({type:z.literal("image_layout"),elementId:z.uuid(),layout:z.enum(["fit_page","hero","portrait","full_width"]),rationale:z.string().trim().min(1).max(240)}).strict(),
  z.object({type:z.literal("update_element"),elementId:z.uuid(),fill:canvasHexSchema.optional(),fontFamily:z.enum(["Inter","Arial","Georgia","Helvetica","Times New Roman","Trebuchet MS","Verdana"]).optional(),fontSize:z.number().finite().min(4).max(180).optional(),fontWeight:z.union([z.literal(400),z.literal(700)]).optional(),opacity:z.number().finite().min(.1).max(1).optional(),rationale:z.string().trim().min(1).max(240)}).strict().refine((value)=>value.fill!==undefined||value.fontFamily!==undefined||value.fontSize!==undefined||value.fontWeight!==undefined||value.opacity!==undefined,{message:"Element updates require at least one supported property."})
]);
export const canvasAiRequestSchema=z.object({command:z.string().trim().min(3).max(500),expectedVersion:z.number().int().nonnegative(),document:canvasDocumentSchema}).strict();
export const canvasAiGeneratedProposalSchema=z.object({summary:z.string().trim().min(1).max(500),actions:z.array(canvasAiGeneratedActionSchema).min(1).max(12)}).strict();
export type CanvasAiAction=z.infer<typeof canvasAiGeneratedActionSchema>&{actionId:string};
export interface CanvasAiProposal{summary:string;actions:CanvasAiAction[];model:string;provider:string;costUsd:number}

export interface SchoolSummary {
  id: string;
  name: string;
  slug: string;
  role: MemberRole;
  primaryColor: string;
}

export const updateSchoolBrandKitSchema = z.object({
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  accentColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  headingFont: z.enum(["Inter", "Arial", "Georgia", "Helvetica", "Times New Roman", "Trebuchet MS", "Verdana"]),
  bodyFont: z.enum(["Inter", "Arial", "Georgia", "Helvetica", "Times New Roman", "Trebuchet MS", "Verdana"])
}).strict();
export type UpdateSchoolBrandKit = z.infer<typeof updateSchoolBrandKitSchema>;
export interface SchoolBrandKit extends UpdateSchoolBrandKit { schoolId: string; updatedAt: string }

export interface UserContext {
  user: { id: string; email: string | null; displayName: string | null };
  schools: SchoolSummary[];
}

export interface PublicationSummary {
  id: string;
  schoolId: string;
  title: string;
  type: PublicationType;
  status: PublicationStatus;
  academicYear: string;
  targetPageCount: number;
  pageCount: number;
  approvedPageCount: number;
  updatedAt: string;
}

export interface PublicationPage {
  id: string;
  sectionId: string;
  pageNumber: number;
  pageType: PageType;
  title: string;
  status: PageStatus;
  templateId: string | null;
  content: Record<string, unknown>;
  designOverrides: Record<string, unknown>;
  rowVersion: number;
}

export interface PublicationDetail extends PublicationSummary {
  subtitle: string;
  language: string;
  vibePreset: string;
  vibeDescription: string;
  orderVersion: number;
  designSystem: Record<string, unknown>;
  sections: Array<{ id: string; title: string; sectionKey: string; position: number }>;
  pages: PublicationPage[];
}

export interface DashboardData {
  school: SchoolSummary;
  activePublication: PublicationSummary | null;
  counts: { needsContent: number; needsReview: number; issues: number };
}

export const contributorAssignmentStatusSchema = z.enum(["assigned", "submitted", "changes_requested", "approved"]);
export type ContributorAssignmentStatus = z.infer<typeof contributorAssignmentStatusSchema>;

export const createContributorInvitationSchema = z.object({
  email: z.email().trim().toLowerCase().max(320),
  assignments: z.array(z.object({
    pageId: z.uuid(),
    instructions: z.string().trim().max(2000).default(""),
    dueAt: z.iso.datetime().nullable().default(null)
  }).strict()).min(1).max(50)
}).strict().superRefine((value, ctx) => {
  if (new Set(value.assignments.map((item) => item.pageId)).size !== value.assignments.length) ctx.addIssue({ code: "custom", path: ["assignments"], message: "Assign each page once." });
});
export type CreateContributorInvitation = z.infer<typeof createContributorInvitationSchema>;

export const acceptContributorInvitationSchema = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{43}$/) }).strict();
export const contributorSubmissionSchema = z.object({ content: pageContentSchema, note: z.string().trim().max(2000).default("") }).strict();
export type ContributorSubmissionInput = z.infer<typeof contributorSubmissionSchema>;
export const contributorReviewSchema = z.object({
  decision: z.enum(["approved", "changes_requested"]),
  feedback: z.string().trim().max(2000).default("")
}).strict();
export type ContributorReviewInput = z.infer<typeof contributorReviewSchema>;

export interface ContributorAssignment {
  id: string;
  publicationId: string;
  publicationTitle: string;
  pageId: string;
  pageNumber: number;
  pageTitle: string;
  pageType: PageType;
  pageStatus: PageStatus;
  pageContent: Record<string, unknown>;
  templateId: string | null;
  designOverrides: Record<string, unknown>;
  rowVersion: number;
  contributorEmail: string;
  contributorUserId: string | null;
  status: ContributorAssignmentStatus;
  instructions: string;
  dueAt: string | null;
  latestSubmission: { id: string; versionNumber: number; content: Record<string, unknown>; note: string; createdAt: string } | null;
  latestReview: { decision: "approved" | "changes_requested"; feedback: string; createdAt: string } | null;
}

export interface ContributorInvitationResult { invitationId: string; inviteUrl: string; expiresAt: string }

export const createPageCommentSchema = z.object({ body: z.string().trim().min(1).max(4000) }).strict();
export const resolvePageCommentSchema = z.object({ resolved: z.boolean() }).strict();
export const pageAssignmentRoleSchema = z.enum(["owner", "reviewer"]);
export const assignPageStaffSchema = z.object({
  userId: z.uuid(),
  role: pageAssignmentRoleSchema,
  dueAt: z.iso.datetime().nullable().default(null)
}).strict();
export type AssignPageStaffInput = z.infer<typeof assignPageStaffSchema>;

export interface SchoolMemberOption {
  userId: string;
  role: MemberRole;
}

export interface PageComment {
  id: string;
  pageId: string;
  body: string;
  status: "open" | "resolved";
  authorId: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PageStaffAssignment {
  id: string;
  pageId: string;
  userId: string;
  role: z.infer<typeof pageAssignmentRoleSchema>;
  dueAt: string | null;
  assignedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CollaborationEvent {
  id: string;
  pageId: string;
  type: "comment_created" | "comment_resolved" | "comment_reopened" | "staff_assigned" | "staff_unassigned" | "page_submitted" | "page_approved" | "page_changes_requested";
  actorId: string;
  subjectUserId: string | null;
  commentId: string | null;
  details: Record<string, unknown>;
  createdAt: string;
}

export const pageReviewDecisionSchema = z.enum(["submit", "approve", "request_changes"]);
export const pageReviewInputSchema = z.object({ expectedVersion: z.number().int().nonnegative(), decision: pageReviewDecisionSchema, note: z.string().trim().max(2000).default("") }).strict();

export interface PublicationCollaboration {
  members: SchoolMemberOption[];
  comments: PageComment[];
  assignments: PageStaffAssignment[];
  events: CollaborationEvent[];
}

export const exportJobFormatSchema = z.enum(["digital_pdf", "print_pdf", "html"]);
export type ExportJobFormat = z.infer<typeof exportJobFormatSchema>;
export type ExportJobStatus = "queued" | "processing" | "succeeded" | "failed";
export const exportSourceManifestSchema = z.object({ schemaVersion: z.literal(1), publicationId: z.uuid(), pages: z.array(z.object({ pageId: z.uuid(), pageVersion: z.number().int().nonnegative(), canvasVersion: z.number().int().nonnegative().nullable() }).strict()).max(300) }).strict();
export type ExportSourceManifest = z.infer<typeof exportSourceManifestSchema>;
export interface PublicationExportJob {
  id: string;
  publicationId: string;
  format: ExportJobFormat;
  status: ExportJobStatus;
  progress: number;
  attempts: number;
  filename: string | null;
  size: number | null;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  downloadUrl: string | null;
  artifactSha256: string | null;
  sourceManifest: ExportSourceManifest | null;
}

export const publicationProofDecisionInputSchema = z.object({ exportJobId: z.uuid(), decision: z.enum(["approved", "changes_requested"]), note: z.string().trim().max(4000).default("") }).strict();
export type PublicationProofDecisionInput = z.infer<typeof publicationProofDecisionInputSchema>;
export interface PublicationProofDecision { id: string; publicationId: string; exportJobId: string; decision: "approved" | "changes_requested"; note: string; decidedBy: string; createdAt: string; artifactFilename: string; artifactSize: number; artifactSha256: string; artifactUrl: string; ticketUrl: string | null; isCurrent: boolean }
export interface PublicationProofState { decisions: PublicationProofDecision[] }

export const supportAuditEventTypeSchema = z.enum(["export_requested", "export_succeeded", "export_failed", "proof_approved", "proof_changes_requested", "publication_published", "publication_unpublished"]);
export type SupportAuditEventType = z.infer<typeof supportAuditEventTypeSchema>;
export interface SupportAuditEvent {
  id: string;
  publicationId: string | null;
  actorId: string | null;
  eventType: SupportAuditEventType;
  resourceId: string | null;
  requestId: string | null;
  details: Record<string, unknown>;
  createdAt: string;
}
export interface SupportOverview {
  schoolId: string;
  role: "owner" | "admin";
  health: { activeExports: number; failedExports7d: number; successfulExports7d: number; proofApprovals7d: number };
  controls: { generalRequestsPerMinute: number; expensiveActionsPerMinute: number; telemetry: "workers_logs_and_analytics_engine" };
  recentEvents: SupportAuditEvent[];
}

export type PrintIssueSeverity = "error" | "warning";
export type PrintIssueCategory = "content" | "image" | "bleed" | "safe_area" | "font" | "system";
export interface PrintIssue { code: string; severity: PrintIssueSeverity; category?: PrintIssueCategory; message: string; pageId?: string; pageNumber?: number; elementId?: string; value?: number; threshold?: number }
export interface PrintValidationResult { publicationId: string; mode: "digital" | "print"; ready: boolean; pageCount: number; errors: number; warnings: number; issues: PrintIssue[] }

export type ApiSuccess<T> = { data: T; requestId: string };
export type ApiFailure = { error: { code: string; message: string; details?: unknown }; requestId: string };

export const DEFAULT_SECTIONS: ReadonlyArray<Omit<SectionPlan, "pageCount">> = [
  { key: "cover", title: "Cover", pageType: "cover" },
  { key: "contents", title: "Table of Contents", pageType: "contents" },
  { key: "principal_message", title: "Principal Message", pageType: "principal_message" },
  { key: "about_school", title: "About Our School", pageType: "about_school" },
  { key: "teachers", title: "Teachers", pageType: "teachers" },
  { key: "class_pages", title: "Class Pages", pageType: "class_page" },
  { key: "achievements", title: "Student Achievements", pageType: "student_achievement" },
  { key: "sports", title: "Sports", pageType: "sports" },
  { key: "events", title: "Events & Trips", pageType: "event" },
  { key: "graduation", title: "Graduation", pageType: "graduation" },
  { key: "acknowledgements", title: "Acknowledgements", pageType: "acknowledgement" },
  { key: "back_cover", title: "Back Cover", pageType: "back_cover" }
] as const;
